// @ts-strict-ignore
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { MultiSelect, TextField, Text } from '@usealloy/component-library';
import { Controller, useFormContext } from 'react-hook-form';
import { getActionName, actionHasDisplayName } from 'flow/service/actions';
import type { ServiceCredentials } from 'flow/store/engine/types';
import type { FormState, ServiceOption } from './types';
import {
  BASE_URL_OPTION_KEY,
  DISPLAY_NAME_OPTION_KEY,
  MODULES_OPTION_KEY,
  WEBHOOK_KEY,
} from 'flow/service/actionOptions';
import {
  createServiceOption, getServiceFailureDefaultOptions, convertActionOptionsToServiceOptions,
  buildAutoSavedRegister,
} from '../serviceOptionHelpers';
import {
  StyledServiceOptionsFormContainer,
  StyledLeftColumnContainer,
  StyledRightColumnContainer,
  StyledSpacer,
} from './styles';
import AdditionalOptions from './Sections/AdditionalOptions';
import RecurringWebhookContainer from './Sections/RecurringWebhook';
import ServiceCredentialsContainer from './Sections/ServiceCredentials';
import { ajaxFetch } from '~/helpers/api';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectIsOnboardingWorkflow, selectFocusedAction, selectRecurringWorkflows,
  selectWorkflowFailureHandlingMode,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { routes } from '~/helpers/routes';
import { getCredentialKeys, buildServiceCredentials } from './Sections/ServiceCredentials/helpers';
import { updateServiceCredentialsInWorkflowProps } from '~/dashboard/views/workflows/store/redux/slice';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import { useDebouncedFunction } from '../../useNodeFormAutoSave';
import usePreventEmptyRegister from '../../usePreventEmptyRegister';
import { failureHandlingDescriptionBestEffortMode, failureHandlingDescriptionFullThresholdMode } from '../../constants';

export default function ServiceOptionsTab({
  handleAutoSave,
}: NodeEditorAutoSaveTabProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const isOnboardingWorkflow = useAppSelector(selectIsOnboardingWorkflow);
  const recurringWorkflowsByService = useAppSelector(selectRecurringWorkflows);
  const failureHandlingModeValue = useAppSelector(selectWorkflowFailureHandlingMode);

  const [credentials, setCredentials] = useState<ServiceCredentials>(null);
  const [isServiceDataFetched, setIsServiceDataFetched] = useState<boolean>(false);

  const serviceFailureOptions = useMemo(() => getServiceFailureDefaultOptions(), []);
  const serviceOptions = useMemo(
    () => focusedAction && convertActionOptionsToServiceOptions(focusedAction.options),
    [focusedAction],
  );

  const fetchServiceData = useCallback(async () => {
    if (!isServiceDataFetched) {
      const internalName = focusedAction?.service?.internalName;

      try {
        const serviceCredentialsSchema = await ajaxFetch(routes.v3.services(internalName));
        if (serviceCredentialsSchema) {
          const builtServiceCredentials = buildServiceCredentials(
            serviceCredentialsSchema, focusedAction?.options,
          );
          // to prevent the base url & modules options from being rendered in the legacy modal,
          // we remove them from credentials here
          if (builtServiceCredentials.credentials[BASE_URL_OPTION_KEY]) {
            // sometimes base url is populated in credentials but not service options
            const filteredServiceOptions = serviceOptions.filter(
              obj => obj.key === BASE_URL_OPTION_KEY,
            );
            if (filteredServiceOptions.length === 0) {
              serviceOptions.push(createServiceOption({
                key: BASE_URL_OPTION_KEY,
                value: builtServiceCredentials.credentials[BASE_URL_OPTION_KEY],
              }));
            }
            delete builtServiceCredentials.credentials[BASE_URL_OPTION_KEY];
          }
          if (builtServiceCredentials.credentials[MODULES_OPTION_KEY]) {
            delete builtServiceCredentials.credentials[MODULES_OPTION_KEY];
          } else if (builtServiceCredentials.credentials[MODULES_OPTION_KEY] === '') {
            // new service case
            serviceOptions.push(createServiceOption({
              key: MODULES_OPTION_KEY,
              value: builtServiceCredentials.credentials[MODULES_OPTION_KEY],
            }));
            delete builtServiceCredentials.credentials[MODULES_OPTION_KEY];
          }
          setCredentials(builtServiceCredentials);

          /**
           * the credentials schema is not included in the initial WF props for any service.
           * they are only fetched from this distinct API call, so when fetching them, we
           * store them under the given service in the WF props.
           */
          dispatch(updateServiceCredentialsInWorkflowProps({
            credentials: serviceCredentialsSchema,
            internalName: focusedAction?.service.internalName,
          }));
        }
      } catch (error) {
        setCredentials(null);
      }
      /**
       * In the case that service credentials could be null
       * for services that do not require credentials (ex: Lists),
       * still need an indicator that data has been fetched in order to initialize options
      */
      setIsServiceDataFetched(true);
    }
  }, [dispatch, isServiceDataFetched, focusedAction, serviceOptions]);

  useEffect(() => {
    fetchServiceData();
  }, [fetchServiceData]);

  const credentialKeys = useMemo(() => (
    credentials?.credential_format && credentials?.credentials
      ? getCredentialKeys(credentials)
      : []
  ), [credentials]);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
  } = useFormContext<FormState>();

  /*
   * Since this form fetches data within it and has a non-traditional render cycle, we need to
   * invoke `handleAutoSave` manually when things change instead of relying on handleNodeFormAutoSave
   * like the other forms
   */
  const debouncedAutoSave = useDebouncedFunction<FormState>({
    fn: formData => {
      /**
       * to prevent the base url & modules service credentials from being rendered in the legacy modal,
       * we adjust them here
       */
      formData.additionalOptions?.forEach(option => {
        if (option.key === BASE_URL_OPTION_KEY || option.key === MODULES_OPTION_KEY) {
          if (!formData.serviceCredentials) {
            formData.serviceCredentials = [];
          }
          formData.serviceCredentials.push(option);
        }
      });
      formData.additionalOptions = formData.additionalOptions?.filter(option => (
        option.key !== BASE_URL_OPTION_KEY && option.key !== MODULES_OPTION_KEY
      ));
      handleAutoSave(formData);
    },
  });
  const submitAutoSave = () => handleSubmit(debouncedAutoSave)();
  const preventEmptyRegister = usePreventEmptyRegister<FormState>();

  /*
   * We extend the register function of the prevent empty register for the `displayName` field to manually
   * invoke auto save without losing the prevent empty functionality.
   */
  const displayNameRegister = buildAutoSavedRegister({
    fieldMethods: preventEmptyRegister('displayName'),
    handleAutoSave: submitAutoSave,
  });


  useEffect(() => {
    const additionalOptions = getValues('additionalOptions');
    const recurringWorkflow = getValues('recurringWorkflow');
    const serviceCredentials = getValues('serviceCredentials');
    const isInitialDataFetched = isServiceDataFetched
      && !additionalOptions
      && !serviceCredentials
      && !recurringWorkflow;

    if (isInitialDataFetched) {
      const initialAdditionalOptions = [];
      let initialRecurringWebhook: ServiceOption = null;
      const initialServiceCredentials = [];

      serviceOptions.forEach(option => {
        /*
          Here we direct and handle action options for specific sections:
          1. to show base url and modules in additional options instead of the credentials modal
          2. handle recurring webhook separately
          3. exclude display name since it has its own form textfield
        */
        if (MODULES_OPTION_KEY === option.key || BASE_URL_OPTION_KEY === option.key) {
          initialAdditionalOptions.push(option);
        } else if (WEBHOOK_KEY === option.key) {
          initialRecurringWebhook = option;
        } else if (credentialKeys.includes(option.key)) {
          initialServiceCredentials.push(option);
        } else if (option.key !== DISPLAY_NAME_OPTION_KEY) {
          initialAdditionalOptions.push(option);
        }
      });
      setValue('additionalOptions', initialAdditionalOptions);
      setValue('recurringWorkflow', initialRecurringWebhook);
      setValue('serviceCredentials', initialServiceCredentials);
    }
  }, [
    setValue, getValues, isServiceDataFetched, credentialKeys, serviceOptions,
  ]);

  const handlingDescription = failureHandlingModeValue === 'full-threshold'
    ? failureHandlingDescriptionFullThresholdMode : failureHandlingDescriptionBestEffortMode;
  return (
    <StyledServiceOptionsFormContainer>
      <StyledLeftColumnContainer>
        <TextField
          label="Display Name"
          type="text"
          style={{ color: !actionHasDisplayName(focusedAction) && 'var(--cl-gray-800)' }}
          disabled
          {...displayNameRegister}
        />
        <Controller
          name="serviceFailureDefaultOption"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <MultiSelect
                isMulti={false}
                label="Service Failure Handling:"
                isDisabled
                onChange={e => {
                  onChange(e);
                  submitAutoSave();
                }}
                options={serviceFailureOptions}
                selectedOptions={value}
              />
              <Text as="span" size={2} style={{ color: 'var(--cl-text-secondary)' }}>
                {handlingDescription}
              </Text>
            </>
          )}
        />
        {isOnboardingWorkflow
          && recurringWorkflowsByService[getActionName(focusedAction)]
          && (
            <Controller
              name="recurringWorkflow"
              control={control}
              render={({ field: { onChange } }) => (
                <RecurringWebhookContainer
                  readOnly
                  initialSelectedRecurringWorkflow={serviceOptions.find(
                    option => option.key === WEBHOOK_KEY)}
                  recurringWorkflowOptions={
                    recurringWorkflowsByService[getActionName(focusedAction)]
                  }
                  onChange={e => {
                    onChange(e);
                    submitAutoSave();
                  }}
                />
              )}
            />
          )}
        <Controller
          name="additionalOptions"
          control={control}
          render={({ field: { onChange, value } }) => (
            <AdditionalOptions
              serviceOptions={value}
              readOnly
              onChange={e => {
                onChange(e);
                submitAutoSave();
              }}
            />
          )}
        />
      </StyledLeftColumnContainer>
      {credentials && (
        <StyledRightColumnContainer>
          <Controller
            name="serviceCredentials"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ServiceCredentialsContainer
                readOnly
                credentials={credentials}
                serviceAction={focusedAction}
                serviceOptions={value}
                onChange={e => {
                  onChange(e);
                  submitAutoSave();
                }}
              />
            )}
          />
        </StyledRightColumnContainer>
      )}
      <StyledSpacer />
    </StyledServiceOptionsFormContainer>
  );
}
